import { useState, useEffect, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, ExternalLink, Copy, ChevronDown, Save, Search, Gauge, FileCode2, Globe2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { GooglePreview } from '../../components/admin/GooglePreview';
import { SearchConsolePanel } from '../../components/admin/SearchConsolePanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const scoreTone = (s) => (s >= 90 ? 'text-emerald-600 bg-emerald-50 border-emerald-200' : s >= 70 ? 'text-amber-600 bg-amber-50 border-amber-200' : 'text-red-600 bg-red-50 border-red-200');
const STATUS_ICON = { ok: [CheckCircle2, 'text-emerald-500'], warn: [AlertTriangle, 'text-amber-500'], error: [XCircle, 'text-red-500'] };

const copy = (text) => navigator.clipboard?.writeText(text).then(() => toast.success('Kopiert'));

function Stat({ label, value, tone = 'text-[#0F172A]', testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{label}</p>
      <p className={`text-3xl font-extrabold mt-2 ${tone}`} data-testid={testid}>{value}</p>
    </div>
  );
}

function ToolLinks({ page }) {
  const enc = encodeURIComponent(page.url);
  const tools = [
    ['Live-Seite', page.url, Globe2],
    ['HTML für Google', `${API}/seo/render?raw=1&path=${encodeURIComponent(page.path)}`, FileCode2],
    ['Rich-Results-Test', `https://search.google.com/test/rich-results?url=${enc}`, Search],
    ['PageSpeed', `https://pagespeed.web.dev/analysis?url=${enc}`, Gauge],
    ['Im Google-Index?', `https://www.google.com/search?q=site:${enc}`, Search],
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {tools.map(([label, href, Icon]) => (
        <a key={label} href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-medium border border-slate-200 rounded-md px-2.5 py-1.5 hover:border-[#066ce7] hover:text-[#066ce7] transition-colors">
          <Icon className="w-3.5 h-3.5" /> {label}
        </a>
      ))}
    </div>
  );
}

function PageRow({ page }) {
  const [open, setOpen] = useState(false);
  const issues = page.checks.filter(c => c.status !== 'ok').length;
  const testid = `seo-row-${page.path.replace(/\//g, '-') || 'home'}`;
  return (
    <div className="border-b border-slate-100 last:border-0" data-testid={testid}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-slate-50 transition-colors" data-testid={`${testid}-toggle`}>
        <span className={`w-12 text-center text-sm font-bold border rounded-md py-1 ${scoreTone(page.score)}`}>{page.score}</span>
        <Badge variant="outline" className="uppercase font-mono text-[10px]">{page.lang}</Badge>
        <span className="font-mono text-sm text-slate-700 w-64 truncate">{page.path}</span>
        <span className="flex-1 text-sm text-slate-500 truncate hidden md:block">{page.title}</span>
        <span className={`text-xs ${issues ? 'text-amber-600' : 'text-emerald-600'}`}>{issues ? `${issues} Hinweis${issues > 1 ? 'e' : ''}` : 'Alles ok'}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-6 grid lg:grid-cols-2 gap-6" data-testid={`${testid}-details`}>
          <div className="space-y-4">
            <GooglePreview url={page.url} title={page.title} description={page.description} />
            <ToolLinks page={page} />
          </div>
          <ul className="space-y-2">
            {page.checks.map(c => {
              const [Icon, tone] = STATUS_ICON[c.status];
              return (
                <li key={c.id} className="flex items-start gap-2.5 text-sm">
                  <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${tone}`} />
                  <span><span className="font-medium text-[#0F172A]">{c.label}</span><span className="text-slate-500"> – {c.detail}</span></span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function Verification({ initial, sitemapUrl, headers }) {
  const [values, setValues] = useState(initial);
  useEffect(() => setValues(initial), [initial]);
  const save = async () => {
    try {
      const saved = await Promise.all(['google_site_verification', 'bing_site_verification'].map(key =>
        axios.put(`${API}/admin/settings`, { key, value: (values[key] || '').trim() }, { headers })));
      setValues(Object.fromEntries(saved.map(r => [r.data.key, r.data.value])));
      toast.success('Bestätigungscodes gespeichert');
    } catch { toast.error('Fehler beim Speichern'); }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="seo-verification">
      <h2 className="text-base font-semibold text-[#0F172A] mb-1">Google Search Console & Bing verbinden</h2>
      <p className="text-sm text-slate-500 mb-5">Dort sehen Sie die echten Ergebnisse: Klicks, Impressionen, Ranking-Position je Suchbegriff und den Indexierungsstatus.</p>
      <ol className="text-sm text-slate-600 space-y-1.5 list-decimal list-inside mb-5">
        <li><a className="text-[#066ce7] hover:underline" href="https://search.google.com/search-console" target="_blank" rel="noreferrer">Search Console</a> öffnen → Property „URL-Präfix“ mit <span className="font-mono text-xs">https://www.itcs-global.com/</span> anlegen</li>
        <li>Bestätigungsmethode „HTML-Tag“ wählen und den Code (oder einfach das komplette <span className="font-mono text-xs">&lt;meta …&gt;</span>-Tag) unten einfügen</li>
        <li>Speichern – der Code ist sofort auf allen Seiten aktiv – dann in der Search Console auf „Bestätigen“ klicken</li>
        <li>Unter „Sitemaps“ diese Adresse einreichen:</li>
      </ol>
      <div className="flex gap-2 mb-6">
        <Input readOnly value={sitemapUrl} className="font-mono text-xs" data-testid="seo-sitemap-url" />
        <Button variant="outline" onClick={() => copy(sitemapUrl)} data-testid="seo-copy-sitemap"><Copy className="w-4 h-4" /></Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <Label className="text-xs">Google Bestätigungscode</Label>
          <Input value={values.google_site_verification || ''} onChange={e => setValues({ ...values, google_site_verification: e.target.value })} placeholder="z.B. a1B2c3D4…" data-testid="seo-google-verification" />
        </div>
        <div>
          <Label className="text-xs">Bing Bestätigungscode (optional)</Label>
          <Input value={values.bing_site_verification || ''} onChange={e => setValues({ ...values, bing_site_verification: e.target.value })} placeholder="z.B. 1234ABCD…" data-testid="seo-bing-verification" />
        </div>
      </div>
      <Button onClick={save} className="bg-[#066ce7] hover:bg-[#005bb5] text-white" data-testid="seo-save-verification"><Save className="w-4 h-4 mr-2" /> Speichern</Button>
    </div>
  );
}

function Routine() {
  const items = [
    ['Wöchentlich', 'Search Console → „Leistung“: Klicks, Impressionen und Position. Filter „Seite“ auf z. B. /leistungen/hallenmontage setzen.'],
    ['Monatlich', 'Search Console → „Seiten“: Sind alle Leistungsseiten indexiert? Neue Fehler? Dieses Cockpit erneut prüfen.'],
    ['Nach Textänderungen', 'Seite hier prüfen, dann in der Search Console per „URL-Prüfung“ die Indexierung neu anfordern.'],
    ['Quartalsweise', 'PageSpeed-Test der Startseite und einer Leistungsseite – Ziel: grüner Bereich auf Mobilgeräten.'],
  ];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="seo-routine">
      <h2 className="text-base font-semibold text-[#0F172A] mb-4">So kontrollieren Sie Ihre SEO-Ergebnisse fortlaufend</h2>
      <ul className="space-y-3">
        {items.map(([when, what]) => (
          <li key={when} className="flex gap-3 text-sm"><span className="font-semibold text-[#066ce7] w-40 shrink-0">{when}</span><span className="text-slate-600">{what}</span></li>
        ))}
      </ul>
      <p className="text-xs text-slate-400 mt-5">Hinweis: Neue Seiten brauchen erfahrungsgemäß einige Wochen, bis sie stabil ranken.</p>
    </div>
  );
}

export default function AdminSEO() {
  const [data, setData] = useState(null);
  const [lang, setLang] = useState('all');
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem('itcs-admin-token')}` }), []);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/seo/audit`, { headers }).then(r => setData(r.data)).catch(() => toast.error('Prüfung fehlgeschlagen')).finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const pages = (data?.pages || []).filter(p => lang === 'all' || p.lang === lang);
  const issues = (data?.pages || []).reduce((n, p) => n + p.checks.filter(c => c.status !== 'ok').length, 0);

  return (
    <div data-testid="admin-seo" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">SEO-Cockpit</h1>
          <p className="text-sm text-slate-500 mt-1">Prüft jede Seite so, wie Google & Co. sie ohne JavaScript sehen – Titel, Beschreibung, Überschriften, Text, Sprachen und strukturierte Daten.</p>
        </div>
        <Button onClick={load} disabled={loading} variant="outline" data-testid="seo-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Neu prüfen
        </Button>
      </div>

      {data && !data.site_url_configured && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="seo-site-url-warning">
          <strong>Tipp für Railway:</strong> Variable <span className="font-mono">SITE_URL=https://www.itcs-global.com</span> setzen, damit alle Canonical-URLs und die Sitemap immer auf die Hauptdomain zeigen (aktuell: {data.base_url}).
        </div>
      )}

      <SearchConsolePanel headers={headers} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label={"Ø SEO-Score"} value={data ? data.average_score : '–'} tone={data ? scoreTone(data.average_score).split(' ')[0] : undefined} testid="seo-average-score" />
        <Stat label={"Geprüfte Seiten"} value={data ? data.pages.length : '–'} testid="seo-page-count" />
        <Stat label={"Offene Hinweise"} value={data ? issues : '–'} tone={issues ? 'text-amber-600' : 'text-emerald-600'} testid="seo-issue-count" />
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Dateien für Google</p>
          <div className="mt-3 space-y-1.5 text-sm">
            <a href={`${API}/seo/sitemap.xml`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[#066ce7] hover:underline" data-testid="seo-sitemap-link">sitemap.xml <ExternalLink className="w-3 h-3" /></a>
            <a href={`${API}/seo/robots.txt`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[#066ce7] hover:underline" data-testid="seo-robots-link">robots.txt <ExternalLink className="w-3 h-3" /></a>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-[#0F172A]">Ergebnisse je Seite</h2>
          <div className="flex gap-1" data-testid="seo-lang-filter">
            {['all', 'de', 'en', 'es'].map(l => (
              <button key={l} onClick={() => setLang(l)} data-testid={`seo-filter-${l}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${lang === l ? 'bg-[#066ce7] text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                {l === 'all' ? 'Alle' : l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        {!data && <p className="p-6 text-sm text-slate-500">Prüfung läuft…</p>}
        {pages.map(p => <PageRow key={p.path} page={p} />)}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Verification initial={data?.verification || {}} sitemapUrl={data?.sitemap_url || ''} headers={headers} />
        <Routine />
      </div>
    </div>
  );
}
